import { useState, useCallback } from "react";
import { createPortal } from "react-dom";

const VARIANTS = {
  success: {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} width={30} height={30}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    iconBg: "#EAF3DE",
    iconColor: "#3B6D11",
    barColor: "#639922",
    textColor: "#3B6D11",
  },
  error: {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} width={30} height={30}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
      </svg>
    ),
    iconBg: "#FCEBEB",
    iconColor: "#A32D2D",
    barColor: "#E24B4A",
    textColor: "#A32D2D",
  },
  info: {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} width={30} height={30}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    iconBg: "#E6F1FB",
    iconColor: "#185FA5",
    barColor: "#378ADD",
    textColor: "#185FA5",
  },
};

const DURATION = 2000;

function PopupUI({ type, title, subtitle, progress }) {
  const v = VARIANTS[type] ?? VARIANTS.success;

  return createPortal(
    <div
      style={{
        position: "fixed",
        inset: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 99999,
        pointerEvents: "none",
        background: "rgba(0,0,0,0.35)",
      }}
    >
      <div
        style={{
          background: "#ffffff",
          borderRadius: "24px",
          padding: "28px 28px 22px",
          width: "260px",
          border: "0.5px solid rgba(0,0,0,0.08)",
          textAlign: "center",
          boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
          animation: "sipensa-popIn .35s cubic-bezier(.34,1.56,.64,1) forwards",
          pointerEvents: "none",
        }}
      >
        <style>{`
          @keyframes sipensa-popIn {
            0%   { transform: scale(0.7); opacity: 0; }
            70%  { transform: scale(1.05); }
            100% { transform: scale(1); opacity: 1; }
          }
        `}</style>

        <div
          style={{
            width: "64px",
            height: "64px",
            borderRadius: "50%",
            background: v.iconBg,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto 14px",
            color: v.iconColor,
          }}
        >
          {v.icon}
        </div>

        <p
          style={{
            margin: "0 0 4px",
            fontSize: "15px",
            fontWeight: 600,
            color: "#111",
          }}
        >
          {title}
        </p>

        {subtitle && (
          <p
            style={{
              margin: "0 0 16px",
              fontSize: "13px",
              color: v.textColor,
            }}
          >
            {subtitle}
          </p>
        )}

        <div
          style={{
            height: "3px",
            background: "rgba(0,0,0,0.08)",
            borderRadius: "99px",
            overflow: "hidden",
            marginTop: subtitle ? 0 : "16px",
          }}
        >
          <div
            style={{
              height: "100%",
              width: `${progress}%`,
              background: v.barColor,
              borderRadius: "99px",
              transition: "width 0.1s linear",
            }}
          />
        </div>
      </div>
    </div>,
    document.body
  );
}

export function usePopup() {
  const [popup, setPopup] = useState(null);
  const [progress, setProgress] = useState(100);

  const show = useCallback((type, title, subtitle) => {
    setProgress(100);
    setPopup({ type, title, subtitle });

    const steps = 20;
    const interval = DURATION / steps;
    let count = 0;

    const timer = setInterval(() => {
      count++;
      setProgress(Math.max(0, 100 - (count / steps) * 100));
      if (count >= steps) {
        clearInterval(timer);
        setPopup(null);
      }
    }, interval);
  }, []);

  const success = useCallback(
    (title, subtitle) => show("success", title, subtitle),
    [show]
  );
  const error = useCallback(
    (title, subtitle) => show("error", title, subtitle),
    [show]
  );
  const info = useCallback(
    (title, subtitle) => show("info", title, subtitle),
    [show]
  );

  const PopupPortal = popup ? (
    <PopupUI
      type={popup.type}
      title={popup.title}
      subtitle={popup.subtitle}
      progress={progress}
    />
  ) : null;

  return { success, error, info, PopupPortal };
}